import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/DataTable/index";
import { DataTableColumn } from "@/components/ui/DataTable/types";
import { ActiveOrderSplitRow } from "./types";
import { buildBaseColumns, DEFAULT_COLUMN_VISIBILITY } from "./columns";

interface ActiveOrdersTableProps {
  rows: ActiveOrderSplitRow[];
  selectedGuid: string | null;
  onOpen: (guidOrderId: string) => void;
  isLoading: boolean;
}

export function ActiveOrdersTable({
  rows,
  selectedGuid,
  onOpen,
  isLoading,
}: ActiveOrdersTableProps) {
  const columns: DataTableColumn<ActiveOrderSplitRow>[] = useMemo(() => {
    const base = buildBaseColumns();

    const actionsCol: DataTableColumn<ActiveOrderSplitRow> = {
      id: "actions",
      header: "",
      size: 90,
      minSize: 80,
      disableRowClick: true,
      cell: (info) => {
        const row = info.row.original;
        const isSelected = row.guid_order_id === selectedGuid;
        return (
          <Button
            variant={isSelected ? "default" : "outline"}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(row.guid_order_id);
            }}
          >
            {isSelected ? "Selected" : "Open"}
          </Button>
        );
      },
      // Aggregate row also offers an Open button, using the parent guid.
      aggregatedCell: (info: any) => {
        const leaves = info.row.getLeafRows() as Array<{ original: ActiveOrderSplitRow }>;
        const first = leaves[0]?.original;
        if (!first) return null;
        const isSelected = first.guid_order_id === selectedGuid;
        return (
          <Button
            variant={isSelected ? "default" : "outline"}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(first.guid_order_id);
            }}
          >
            {isSelected ? "Selected" : "Open"}
          </Button>
        );
      },
    };

    return [actionsCol, ...base];
  }, [selectedGuid, onOpen]);

  return (
    <div className="flex flex-col gap-3 min-w-0 flex-1">
      {isLoading ? (
        <div className="p-4 border rounded-md">
          <p>Loading active orders...</p>
        </div>
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          enableSorting
          enableMultiSort
          enableFiltering
          enableMultiFilter
          enableColumnResizing
          enableColumnVisibility
          enableColumnPinning
          enableGrouping
          enableExport
          defaultCondensed
          groupBy={["guid_order_id"]}
          defaultColumnVisibility={DEFAULT_COLUMN_VISIBILITY}
          defaultSorting={[
            { id: "exchange_event_time", desc: true },
            { id: "instrument_name", desc: false },
          ]}
          emptyMessage="No active open orders."
          stickyHeaders
          tableClassName="max-h-[calc(100vh-260px)] overflow-y-scroll"
        />
      )}
    </div>
  );
}
